import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { documentsRoute } from "app/router";
import { Button, Card, CardTitle, TableView } from "components/elements";
import { SwitchInput, TablePagination, TableSearch } from "components/elements";
import { OrganizationSelector } from "components/form/OrganizationSelector";
import { UploadDocument } from "components/form/UploadDocument";
import { UserSelector } from "components/form/UserSelector";
import { AllDocumentsTable } from "components/layout/documents/AllDocumentsTable";
import { DocumentsTable } from "components/layout/documents/DocumentsTable";
import { useAllOrganizations } from "hooks/useAllOrganizations";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFileList } from "hooks/useFileList";
import { useSortToggler } from "hooks/useSortToggler";
import { useThrottled } from "hooks/useThrottled";
import { useUsers } from "hooks/useUsers";
import { trpc } from "lib/trpc";
import { DocumentsList } from "@compass/shared/types";

type VisibilityFilter = "all" | "private" | "public";

export const Documents = () => {
    const input = useSearch({ from: documentsRoute.id });
    const navigate = useNavigate();
    const throttledNavigate = useThrottled(navigate);

    const visibilityFilter: VisibilityFilter =
        input.showPublicFiles === undefined && input.showPrivateFiles === undefined
            ? "all"
            : input.showPublicFiles && input.showPrivateFiles
              ? "all"
              : input.showPublicFiles
                ? "public"
                : input.showPrivateFiles
                  ? "private"
                  : "all";

    const { data, isInitialLoading, error, nothingFound } = useFileList({
        ...input,
        showPublicFiles: visibilityFilter === "all" || visibilityFilter === "public",
        showPrivateFiles: visibilityFilter === "all" || visibilityFilter === "private",
    });

    const currentUser = useCurrentUser();
    const organizationsList = useAllOrganizations(currentUser);
    const usersList = useUsers({}, currentUser);
    const [searchByOrganization, setSearchByOrganization] = useState<number | null | undefined>(
        input.organizationId ?? undefined,
    );
    const [searchByUser, setSearchByUser] = useState<number | null | undefined>(
        input.authorId ?? undefined,
    );

    const [user] = trpc.users.me.useSuspenseQuery();
    const [isUploadDocumentOpen, setIsUploadDocumentOpen] = useState(false);

    const getDownloadUrl = trpc.userContent.getDownloadUrl.useMutation();
    const downloadDocument = async (fileId: string, type?: string | null) => {
        const { downloadUrl } = await getDownloadUrl.mutateAsync({ fileId, type });
        window.open(downloadUrl, "_blank");
    };

    const [searchValue, setSearchValue] = useState(input.search ?? "");
    const onSearchChange = (search: string) => {
        setSearchValue(search);
        throttledNavigate({
            search: { ...input, search: search === "" ? undefined : search },
            replace: true,
        });
    };

    const onOrganizationChange = (value: number | null | undefined) => {
        setSearchByOrganization(value);
        throttledNavigate({
            search: { ...input, organizationId: value ? value : undefined },
            replace: true,
        });
    };
    const onUserChange = (value: number | null | undefined) => {
        setSearchByUser(value);
        throttledNavigate({
            search: { ...input, authorId: value ? value : undefined },
            replace: true,
        });
    };

    const onVisibilityChange = (filter: VisibilityFilter) => {
        navigate({
            search: {
                ...input,
                showPublicFiles: filter === "all" || filter === "public",
                showPrivateFiles: filter === "all" || filter === "private",
                page: 0,
            },
            replace: true,
        });
    };

    const onToggleMyFiles = (value: boolean) => {
        navigate({
            search: {
                ...input,
                showOnlyMyFiles: value ? true : false,
                page: 0,
            },
            replace: true,
        });
    };

    const onToggleReferenced = (value: boolean) => {
        navigate({
            search: {
                ...input,
                onlyReferenced: value || undefined,
                page: 0,
            },
            replace: true,
        });
    };

    const onClearFilters = () => {
        setSearchValue("");
        setSearchByOrganization(undefined);
        setSearchByUser(undefined);
        navigate({ search: {}, replace: true });
    };

    const onChangePage = (page: number | undefined) => {
        const newPage = page ?? 0;
        navigate({
            search: {
                ...input,
                page: newPage > 0 ? newPage : 0,
            },
            replace: true,
        });
    };

    const onChangePageSize = (pageSize: number | undefined) => {
        const effectiveNewPageSize = pageSize && pageSize > 0 ? pageSize : 10;
        const currentFirstItem = (input.page ?? 0) * (input.pageSize ?? 10);

        const newPage = Math.floor(currentFirstItem / effectiveNewPageSize);

        const maxPage = Math.max(0, Math.ceil((data?.totalFound ?? 0) / effectiveNewPageSize) - 1);

        const finalPage = Math.min(newPage, maxPage);

        navigate({
            search: {
                ...input,
                pageSize: effectiveNewPageSize,
                page: finalPage,
            },
            replace: true,
        });
    };

    const { sort = "desc", sortBy = "uploaded" } = input;
    const sortToggler = useSortToggler<DocumentsList["sortBy"]>({
        defaultSort: "desc",
        defaultSortBy: "uploaded",
    });
    const toggleSortField = (field: DocumentsList["sortBy"]) => {
        const [nextSortBy, nextSort] = sortToggler({
            currentSortBy: sortBy,
            currentSort: sort,
            nextSortBy: field,
        });
        navigate({
            search: { ...input, sortBy: nextSortBy, sort: nextSort },
            replace: true,
        });
    };

    const hasActiveFilters =
        !!input.search ||
        !!input.organizationId ||
        !!input.authorId ||
        input.showOnlyMyFiles === true ||
        !!input.onlyReferenced ||
        !!input.aiStatus ||
        visibilityFilter !== "all";

    return (
        <Card>
            <CardTitle
                actions={
                    <UploadDocument
                        isOpen={isUploadDocumentOpen}
                        setIsOpen={setIsUploadDocumentOpen}
                        notifyContext="documents"
                        button={
                            <Button variant="primary" className="h-[42px] whitespace-nowrap">
                                Upload New Document
                            </Button>
                        }
                    />
                }
            >
                <div className="font-sans text-[28px] font-semibold tracking-normal text-[#424242]">
                    Documents
                </div>
            </CardTitle>

            <div className="flex flex-wrap items-center gap-3 pb-4">
                <TableSearch value={searchValue} onChange={onSearchChange} />

                <select
                    className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700"
                    value={visibilityFilter}
                    onChange={(e) => onVisibilityChange(e.target.value as VisibilityFilter)}
                >
                    <option value="private">Private Only</option>
                    <option value="public">Public Only</option>
                    <option value="all">All Files</option>
                </select>

                {currentUser?.isCompassUser && (
                    <OrganizationSelector
                        inputClassName="h-9"
                        withClear
                        value={searchByOrganization}
                        onChange={onOrganizationChange}
                        organizations={organizationsList?.data ?? []}
                    />
                )}
                {currentUser?.isCompassUser && (
                    <UserSelector
                        inputClassName="h-9"
                        withClear
                        value={searchByUser}
                        onChange={onUserChange}
                        users={usersList?.data?.users ?? []}
                    />
                )}

                <SwitchInput
                    name="showOnlyMyFiles"
                    label="My files only"
                    value={input.showOnlyMyFiles ?? false}
                    onChange={onToggleMyFiles}
                />

                <SwitchInput
                    name="onlyReferenced"
                    label="Referenced only"
                    value={input.onlyReferenced ?? false}
                    onChange={onToggleReferenced}
                />

                <select
                    className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700"
                    value={input.aiStatus ?? "all"}
                    onChange={(e) => {
                        const val = e.target.value as "all" | "enabled" | "disabled";
                        navigate({
                            search: {
                                ...input,
                                aiStatus: val === "all" ? undefined : val,
                                page: 0,
                            },
                            replace: true,
                        });
                    }}
                >
                    <option value="all">AI: All</option>
                    <option value="enabled">AI: Enabled</option>
                    <option value="disabled">AI: Disabled</option>
                </select>

                {hasActiveFilters && (
                    <button
                        onClick={onClearFilters}
                        className="h-9 rounded-md px-3 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            <TableView
                data={data}
                emptyCollection={<div>There are no documents yet. Try adding one?</div>}
                error={error}
                footer={
                    <TablePagination
                        total={data?.totalFound}
                        page={input.page}
                        pageSize={input.pageSize}
                        onChangePage={onChangePage}
                        onChangePageSize={onChangePageSize}
                    />
                }
                isInitialLoading={isInitialLoading}
                isFiltering={Object.keys(input).filter((key) => key !== "_empty").length > 0}
                nothingFound={nothingFound}
            >
                {(data) => (
                    <AllDocumentsTable
                        documents={data.documents}
                        onDownload={downloadDocument}
                        sort={sort}
                        sortBy={sortBy}
                        toggleSortField={toggleSortField}
                    />
                )}
            </TableView>
        </Card>
    );
};
